import { useEffect, useState } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { userRoleService } from '@/services/user_role_service'
import { toast } from 'sonner'
import { UserRole } from '@magnetar/admin-api-interfaces'
import { useAuth } from '@/hooks/useAuth'
import { canConfirmRoleDeletion, type RoleDeletionImpact } from './roleDeletion'

interface DeleteAlertProps {
  role: UserRole
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

export function DeleteAlert({ role, open, onOpenChange, onSuccess }: DeleteAlertProps) {
  const { token } = useAuth()
  const [impact, setImpact] = useState<RoleDeletionImpact | null>(null)
  const [loadingImpact, setLoadingImpact] = useState(false)

  // 開啟時先打 deletion-impact：列出使用中的帳號(擋)／主檔角色將自動修復的帳號／無法修復的帳號(擋)
  useEffect(() => {
    if (!open) {
      setImpact(null)
      return
    }
    let cancelled = false
    setLoadingImpact(true)
    userRoleService.getRoleDeletionImpact(role, token).then((result) => {
      if (cancelled) return
      setLoadingImpact(false)
      if (result.success && result.data) {
        setImpact(result.data)
      } else {
        toast.error(result.error?.message || '無法取得刪除影響')
      }
    })
    return () => {
      cancelled = true
    }
  }, [open, role, token])

  const handleDelete = async () => {
    try {
      const result = await userRoleService.deleteUserRole(role, token)

      if (result.success) {
        toast.success(`角色 ${role.role_name} 已刪除`)
        onSuccess()
        onOpenChange(false)
      } else {
        toast.error(result.error?.message || '刪除角色失敗')
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '刪除角色失敗'
      toast.error(message)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>確定要刪除此角色？</AlertDialogTitle>
          <AlertDialogDescription>
            此操作無法復原。這將永久刪除角色 {role.role_name} 及其所有相關權限設定。
          </AlertDialogDescription>
        </AlertDialogHeader>

        {loadingImpact && <p className="text-sm text-muted-foreground">正在檢查此角色的使用狀況…</p>}

        {impact && impact.activeUsers.length > 0 && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm">
            <p className="font-medium text-destructive">
              仍有 {impact.activeUsers.length} 個帳號使用此角色，請先將他們改派到其他角色：
            </p>
            <ul className="mt-1 list-inside list-disc text-destructive">
              {impact.activeUsers.map((u) => (
                <li key={u.user_id}>
                  {u.account}（{u.user_name}）
                </li>
              ))}
            </ul>
          </div>
        )}

        {impact && impact.orphanUsers.length > 0 && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm">
            <p className="font-medium text-destructive">
              以下帳號的主檔角色指向此角色且無法自動修復，請先手動處理：
            </p>
            <ul className="mt-1 list-inside list-disc text-destructive">
              {impact.orphanUsers.map((u) => (
                <li key={u.user_id}>
                  {u.account}（{u.user_name}）
                </li>
              ))}
            </ul>
          </div>
        )}

        {impact && impact.staleUsersToRepair.length > 0 && (
          <div className="rounded-md border border-yellow-500/50 bg-yellow-500/10 p-3 text-sm">
            <p className="font-medium text-yellow-700 dark:text-yellow-400">
              以下帳號的主檔角色已過期指向此角色，確認刪除後會自動改指回其實際角色：
            </p>
            <ul className="mt-1 list-inside list-disc text-yellow-700 dark:text-yellow-400">
              {impact.staleUsersToRepair.map((u) => (
                <li key={u.user_id}>
                  {u.account}（{u.user_name}）→ {u.repair_to_role_name}
                </li>
              ))}
            </ul>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel>取消</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={loadingImpact || !canConfirmRoleDeletion(impact)}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            刪除
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
